/**
 * ContextCache — Client-side cache for data objects the LLM has accessed.
 *
 * Stores entries keyed by typed identifiers (e.g. "view:40170", "entity:40071").
 * Before each user message, all entries are refreshed by calling the registered
 * tool handlers so the LLM always receives the latest data.
 */
export class ContextCache {
    constructor() {
        this.entries = new Map();
        this.channelId = null;
        this.toolExecutor = null;
    }
    /**
     * Unwrap SDK envelope { data: ..., status: "ok" } for new tools.
     * Old tools (getViewData, getModelByUnnyId) return raw data without envelope.
     */
    unwrapEnvelope(raw) {
        if (raw && typeof raw === 'object' && 'status' in raw && 'data' in raw) {
            return raw.data;
        }
        return raw;
    }
    /**
     * Provide the tool executor so refreshAll/restoreFromKeys can call
     * the registered handlers (getViewData, getModelByUnnyId, etc.).
     */
    setToolExecutor(executor) {
        this.toolExecutor = executor;
    }
    /** Set the active channel (called when user opens/switches channels) */
    setChannel(channelId) {
        this.channelId = channelId;
    }
    /** Get the active channel */
    getChannel() {
        return this.channelId;
    }
    /** Add or update a cache entry */
    set(key, data, label) {
        this.entries.set(key, { key, data, label });
    }
    /** Remove an entry */
    delete(key) {
        this.entries.delete(key);
    }
    /** Get all entries as an array (for sending to backend) */
    getAll() {
        return Array.from(this.entries.values());
    }
    /** Get the list of keys (for persistence) */
    getKeys() {
        return Array.from(this.entries.keys());
    }
    /** Get number of entries */
    get size() {
        return this.entries.size;
    }
    /** Clear the cache (on channel switch or disconnect) */
    clear() {
        this.entries.clear();
    }
    /** Remap entity cache keys after save (new_item_* → real IDs) */
    remapEntityIds(idMapping) {
        const remapped = [];
        for (const [oldId, newId] of Object.entries(idMapping)) {
            const oldKey = `entity:${oldId}`;
            const newKey = `entity:${newId}`;
            if (this.entries.has(oldKey)) {
                const entry = this.entries.get(oldKey);
                this.entries.delete(oldKey);
                entry.key = newKey;
                entry.label = entry.label.replace(oldId, newId);
                if (entry.data && typeof entry.data === 'object') {
                    entry.data.$unnyId = newId;
                }
                this.entries.set(newKey, entry);
                remapped.push(`${oldId} → ${newId}`);
            }
        }
        return remapped;
    }
    /**
     * Refresh all entries by calling the appropriate tool handlers.
     * Each entry type maps to a specific tool:
     *   - "view:*"   → getViewData({ unnyId })
     *   - "entity:*" → getModelByUnnyId({ unnyId })
     *
     * Failed refreshes (deleted items) are evicted from the cache.
     * All refreshes run in parallel.
     */
    async refreshAll() {
        if (!this.toolExecutor || this.entries.size === 0)
            return;
        console.log(`[ContextCache] Refreshing ${this.entries.size} entries: ${Array.from(this.entries.keys()).join(', ')}`);
        const refreshPromises = Array.from(this.entries.keys()).map(async (key) => {
            const colonIdx = key.indexOf(':');
            if (colonIdx === -1)
                return;
            const type = key.substring(0, colonIdx);
            const id = key.substring(colonIdx + 1);
            if (!id)
                return;
            try {
                let toolName;
                let toolArgs;
                if (type === 'view') {
                    toolName = 'getViewData';
                    toolArgs = { unnyId: id };
                }
                else if (type === 'entity') {
                    toolName = 'getModelByUnnyId';
                    toolArgs = { unnyId: id };
                }
                else if (type === 'script') {
                    toolName = 'getScriptCode';
                    toolArgs = { id };
                }
                else if (type === 'image') {
                    toolName = 'getImageAsset';
                    toolArgs = { id };
                }
                else if (type === 'view-metadata') {
                    toolName = 'getViewMetadata';
                    toolArgs = { unnyId: id };
                }
                else if (type === 'localization') {
                    toolName = 'getLocalizationStringByKey';
                    toolArgs = { key: id }; // id is "GROUP/KEY"
                }
                else if (type === 'template') {
                    toolName = 'getTemplateList';
                    toolArgs = { templateName: id };
                }
                else if (type === 'products') {
                    toolName = 'getProducts';
                    toolArgs = {};
                }
                else {
                    // Unknown cache type — skip
                    return;
                }
                console.log(`[ContextCache] Refreshing ${key} via ${toolName}`);
                // Per-item timeout to prevent individual hangs
                const ITEM_TIMEOUT = 8000; // 8 seconds per item
                const result = await Promise.race([
                    this.toolExecutor(toolName, toolArgs),
                    new Promise((resolve) => setTimeout(() => resolve({
                        success: false,
                        error: `Refresh timeout for ${key}`,
                    }), ITEM_TIMEOUT)),
                ]);
                if (result.success && result.result != null) {
                    // Unwrap envelope for new tool types
                    const needsUnwrap = type !== 'view' && type !== 'entity';
                    const payload = needsUnwrap
                        ? this.unwrapEnvelope(result.result)
                        : result.result;
                    if (payload == null) {
                        // Data was null inside envelope — item deleted, evict
                        this.entries.delete(key);
                    }
                    else if (type === 'template' && Array.isArray(payload)) {
                        // Special handling for template refresh: getTemplateList returns array
                        if (payload.length > 0) {
                            const entry = this.entries.get(key);
                            if (entry) {
                                entry.data = payload[0];
                            }
                        }
                        else {
                            // Template was deleted — evict
                            this.entries.delete(key);
                        }
                    }
                    else {
                        // Update the data in-place, preserve the label
                        const entry = this.entries.get(key);
                        if (entry) {
                            entry.data = payload;
                        }
                    }
                    if (this.entries.has(key)) {
                        console.log(`[ContextCache] Refreshed ${key} successfully`);
                    }
                }
                else {
                    // Item was deleted or handler failed — remove from cache
                    console.warn(`[ContextCache] Refresh failed for ${key}: ${result.error}`);
                    this.entries.delete(key);
                }
            }
            catch (error) {
                // Handler failed — remove stale entry
                console.warn(`[ContextCache] Failed to refresh ${key}:`, error);
                this.entries.delete(key);
            }
        });
        await Promise.all(refreshPromises);
        console.log(`[ContextCache] Refresh complete, ${this.entries.size} entries remain`);
    }
    /**
     * Restore cache from a list of keys (after page reload).
     * Calls the appropriate handlers to re-fetch all items.
     * Items that no longer exist are silently skipped.
     */
    async restoreFromKeys(keys) {
        if (!this.toolExecutor || keys.length === 0)
            return;
        const restorePromises = keys.map(async (key) => {
            const colonIdx = key.indexOf(':');
            if (colonIdx === -1)
                return;
            const type = key.substring(0, colonIdx);
            const id = key.substring(colonIdx + 1);
            if (!id)
                return;
            try {
                let toolName;
                let toolArgs;
                if (type === 'view') {
                    toolName = 'getViewData';
                    toolArgs = { unnyId: id };
                }
                else if (type === 'entity') {
                    toolName = 'getModelByUnnyId';
                    toolArgs = { unnyId: id };
                }
                else if (type === 'script') {
                    toolName = 'getScriptCode';
                    toolArgs = { id };
                }
                else if (type === 'image') {
                    toolName = 'getImageAsset';
                    toolArgs = { id };
                }
                else if (type === 'view-metadata') {
                    toolName = 'getViewMetadata';
                    toolArgs = { unnyId: id };
                }
                else if (type === 'localization') {
                    toolName = 'getLocalizationStringByKey';
                    toolArgs = { key: id };
                }
                else if (type === 'template') {
                    toolName = 'getTemplateList';
                    toolArgs = { templateName: id };
                }
                else if (type === 'products') {
                    toolName = 'getProducts';
                    toolArgs = {};
                }
                else {
                    return;
                }
                const result = await this.toolExecutor(toolName, toolArgs);
                if (result.success && result.result != null) {
                    // Unwrap envelope for new tool types
                    const needsUnwrap = type !== 'view' && type !== 'entity';
                    const data = (needsUnwrap ? this.unwrapEnvelope(result.result) : result.result);
                    if (data == null)
                        return; // null inside envelope — item deleted
                    let label;
                    if (type === 'view') {
                        label = `"${data.name || 'View'}" view`;
                    }
                    else if (type === 'entity') {
                        label = `"${data.Name || ''}" (${id})`;
                    }
                    else if (type === 'script') {
                        label = `"${data.name || 'Script'}" script`;
                    }
                    else if (type === 'image') {
                        label = `"${data.name || 'Image'}" image`;
                    }
                    else if (type === 'view-metadata') {
                        label = `"${data.name || 'View'}" view metadata`;
                    }
                    else if (type === 'localization') {
                        label = `localization "${id}"`;
                    }
                    else if (type === 'template') {
                        // getTemplateList returns array — take first element
                        if (Array.isArray(data) && data.length > 0) {
                            const template = data[0];
                            this.entries.set(key, {
                                key,
                                data: template,
                                label: `template "${template.displayName || template.name}"`,
                            });
                            return;
                        }
                        else if (Array.isArray(data) && data.length === 0) {
                            return; // Template deleted — skip
                        }
                        label = `template "${data.displayName || data.name || id}"`;
                    }
                    else if (type === 'products') {
                        const count = Array.isArray(data) ? data.length : 0;
                        label = `all products (${count})`;
                    }
                    else {
                        label = key;
                    }
                    this.entries.set(key, { key, data, label });
                }
                // If item no longer exists, simply don't add it
            }
            catch (error) {
                console.warn(`[ContextCache] Failed to restore ${key}:`, error);
                // Silently skip
            }
        });
        await Promise.all(restorePromises);
        if (this.entries.size > 0) {
            console.log(`[ContextCache] Restored ${this.entries.size} items from ${keys.length} keys`);
        }
    }
}
